import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  CalendarDays, 
  Users, 
  FolderOpen, 
  Calendar, 
  Clock, 
  AlertTriangle 
} from 'lucide-react';

// Import sub-components
import MyAvailabilityTab from './MyAvailabilityTab';
import PendingRequestsTab from './PendingRequestsTab';
import TeamAvailabilityTab from './TeamAvailabilityTab';
import ProjectPlanningTab from './ProjectPlanningTab';
import UnifiedOverviewTab from './UnifiedOverviewTab';
import useSubTabShortcut from '../../hooks/useSubTabShortcut';

const ManpowerPlanning = ({ currentUser, employees, projects, settings }) => {
  const { t } = useTranslation();
  const isAdmin = currentUser?.role === 'admin';
  const isManager = currentUser?.role === 'manager';
  const isAdminOrManager = isAdmin || isManager;

  const [activeTab, setActiveTab] = useState('my-availability');
  useSubTabShortcut({
    tab: 'manpower', ctxKey: 'manpowerSubTab', value: activeTab, setValue: setActiveTab,
    labels: { 'my-availability': 'auto.ctr.myAvailability', 'team-overview': 'auto.ctr.teamOverview',
      'project-planning': 'auto.ctr.projectPlanning', 'unified-view': 'auto.ctr.unifiedView',
      'pending-requests': 'auto.ctr.pendingRequests' },
    allowed: isAdminOrManager
      ? ['my-availability', 'team-overview', 'project-planning', 'unified-view', ...(isAdmin ? ['pending-requests'] : [])]
      : ['my-availability'],
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('auto.ctr.manpowerPlanning')}</h2>
          <p className="text-gray-600">{t('auto.ctr.manageEmployeeAvailabilityAndProjectStaffing')}</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="my-availability" className="flex items-center gap-2" data-testid="tab-my-availability">
            <CalendarDays className="h-4 w-4" />
            {t('auto.ctr.myAvailability')}
          </TabsTrigger>
          {isAdminOrManager && (
            <>
              <TabsTrigger value="team-overview" className="flex items-center gap-2" data-testid="tab-team-overview">
                <Users className="h-4 w-4" />
                {t('auto.ctr.teamOverview')}
              </TabsTrigger>
              <TabsTrigger value="project-planning" className="flex items-center gap-2" data-testid="tab-project-planning">
                <FolderOpen className="h-4 w-4" />
                {t('auto.ctr.projectPlanning')}
              </TabsTrigger>
              <TabsTrigger value="unified-view" className="flex items-center gap-2" data-testid="tab-unified-view">
                <Calendar className="h-4 w-4" />
                {t('auto.ctr.unifiedView')}
              </TabsTrigger>
              {isAdmin && (
                <TabsTrigger value="pending-requests" className="flex items-center gap-2" data-testid="tab-pending-requests">
                  <Clock className="h-4 w-4" />
                  {t('auto.ctr.pendingRequests')}
                </TabsTrigger>
              )}
            </>
          )}
        </TabsList>

        <TabsContent value="my-availability">
          <MyAvailabilityTab currentUser={currentUser} isAdmin={isAdmin} />
        </TabsContent>

        {isAdminOrManager && (
          <>
            <TabsContent value="team-overview">
              <TeamAvailabilityTab employees={employees} />
            </TabsContent>
            
            <TabsContent value="project-planning">
              <ProjectPlanningTab 
                projects={projects} 
                employees={employees} 
                settings={settings}
              />
            </TabsContent>
            
            <TabsContent value="unified-view">
              <UnifiedOverviewTab projects={projects} employees={employees} />
            </TabsContent>
          </>
        )}

        {isAdmin && (
          <TabsContent value="pending-requests">
            <PendingRequestsTab />
          </TabsContent>
        )}
      </Tabs>

      {/* Info Card for Employees */}
      {!isAdminOrManager && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800">{t('auto.ctr.aboutAvailabilityRequests')}</p>
                <p className="text-sm text-blue-700">
                  Your availability requests need to be approved by an administrator. 
                  Please submit your planned leaves in advance to ensure proper project planning.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ManpowerPlanning;
